import { Services } from "./services";

import {
  B2cBrand,
  B2cCategoryTreePayload,
  B2cCustomerChannelPayload,
  B2cProductPayload,
  B2cProductSearchPayload,
  B2cStartupPayload,
  B2cUrlDataPayload,
  Currency,
  FeaturedProductsDocument,
  HolidayEvent,
  StartupDocument,
  StartupInput,
} from "@/generated/graphql.lazy/b2c";
import i18next from "i18next";
import { LocationHook, RouteHook } from "preact-iso";
import { Url } from "@/helpers/url";
import { defaultVatCoefficient } from "@/constants/vat";
import { createProductPath } from "@/util/category";
import { exec } from "@/util/preact-iso";
import { IconResolver } from "icon-resolver";

type InitializationState =
  { state: 'none' } |
  { state: 'initialized', hash: string, invalidated: boolean };

export class Initialize {
  protected initialization: InitializationState;
  protected analyticsStarted = false;

  protected lastLoadedPageParametersHash: null | string = null;
  protected resolveIdlePromise!: (value: unknown) => void;
  protected idlePromise = new Promise((resolve) => {
    this.resolveIdlePromise = resolve;
  });

  constructor(protected services: Services) {
    this.initialization = { state: 'none' };

    if (!import.meta.env.SSR) {
      window.addEventListener('message', event => {
        if (event.data.type === 'idle_initialize') {
          this.resolveIdlePromise(null);
        }
      });
    }
  }

  async getStartupPayload(input: StartupInput) {
    if (this.services.preloaded.startup) {
      return this.services.preloaded.startup as B2cStartupPayload;
    }

    const response = await this.services.api.client.query({
      query: StartupDocument,
      variables: {
        input: input,
      },
    });

    return response.data.Startup as B2cStartupPayload;
  }

  async findUpdatedPathForEntity(entity: { id: string; type: "brand" | "category" | "product" }) {
    const { cache, categories, brands } = this.services;

    if (entity.type === "brand") {
      return [brands.brands.get(entity.id)!.slug];
    }

    if (entity.type === "category") {
      return categories.getItemPath(entity.id);
    }

    if (entity.type === "product") {
      const product = cache.getProduct(entity.id);

      return createProductPath(product, categories);
    }
  }

  async prepare(params: { location: LocationHook, route: RouteHook }) {
    const firstVisit = this.initialization.state === 'none';
    const initialize = this.initialization.state === 'none' || (this.initialization.state === 'initialized' && this.initialization.invalidated);
    // @ts-expect-error
    const isCartPage = params.route['data-route'] === "cart-page";

    if (firstVisit) {
      Url.setRoute(params.location.route);

      await Promise.all([
        this.services.preloaded.load(),
        this.services.parameters.migrateParameters()
      ]);

      if (this.services.preloaded.simpleCart) {
        this.services.cart.setPreloadedCart(this.services.preloaded.simpleCart.Cart);
      }
    } else {
      this.services.preloaded.brandsByCategory = null;
      this.services.preloaded.productsSearch = null;
    }

    if (initialize) {
      if (this.initialization.state === 'initialized') {
        this.clearServices();
      }

      this.initialization = {
        state: 'initialized',
        invalidated: false,
        hash: this.getHash(params.route.params)
      };

      const startupQueryParams = this.buildStartupQueryParameters(params);

      const [data] = await Promise.all([
        this.getStartupPayload(startupQueryParams),
        this.services.i18n.load({
          language: startupQueryParams.pageLang
        }),
      ]);

      if (data.urlData) {
        this.applyUrlData(data.urlData, Url.normalizeRoute(params.route.path));
      }

      await this.applyStartupData(data);
    }

    if (this.services.preloaded.featuredProducts) {
      this.services.featuredProducts.setPreloadedProducts(this.services.preloaded.featuredProducts as B2cProductSearchPayload[]);
    }

    if (firstVisit) {
      this.services.parameters.activateDialogIfNeed();
      this.startAnalytics();
    } else if (!isCartPage) {
      await this.services.cart.fetchSimpleCart();
    }
  }

  protected buildStartupQueryParameters(params: { route: RouteHook }) {
    const visitorParameters = this.services.parameters.getVisitorParameters();
    const savedParameters = this.services.parameters.getSavedParameters();
    const dynamicRouteMatch = exec(params.route.path, '/:lang/:dynamicPath+');
    const pageLangParam = params.route.params.lang as string | undefined;

    const parameters: StartupInput = {
      browserLang: visitorParameters.language!,
      preferencesLang: savedParameters.language!,
      preferencesCountry: savedParameters.country?.toUpperCase(),
      preferencesCurrency: savedParameters?.currency!,
    };

    if (dynamicRouteMatch) {
      parameters.url = Url.normalizeRoute(params.route.path);
    }

    if (pageLangParam) {
      parameters.pageLang = pageLangParam;
    }

    return parameters;
  }

  protected applyUrlData(data: B2cUrlDataPayload, route: string) {
    const type = data.type as "product" | "category" | "brand";
    const value = data[type] as { redirectUrl?: string } & B2cProductPayload;
    const alternates = data.alternateUrls ?? [];

    this.services.routes.setForUrl(route, {
      type,
      id: value.id,
    });

    this.services.routes.setAlternates(route, alternates);

    if (type === "product") {
      value.redirectUrl = data.redirectUrl;
      this.services.cache.setProduct(value);
    }

    if (type == "category") {
      this.services.categories.addAdditionalInfoItem({
        id: data.category.id,
        name: data.category.name,
        description: data.category.description,
      });
    }
  }

  protected async applyStartupData(data: B2cStartupPayload) {
    const visitorParameters = this.services.parameters.getVisitorParameters();
    const savedParameters = this.services.parameters.getSavedParameters();

    const customerChannel = data.customerChannel as B2cCustomerChannelPayload;

    this.services.customerChannel.setPreloadedCustomerChannel(customerChannel);

    this.services.parameters.setupDetectedParameters({
      country: data.detectedCountry ?? 'LV',
      language: visitorParameters.language!
    });

    this.services.parameters.setupAppliedParameters({
      language: customerChannel.currentLanguage,
      country: customerChannel.currentCountry.code,
      currency: customerChannel.customer?.currency ?? savedParameters.currency ?? 'EUR'
    });

    // after parameters set; cause Url.to to use them in route generation

    this.services.categories.setPreloadedTree(data.categoriesTree as B2cCategoryTreePayload[]);
    this.services.brands.setPreloadedBrands(data.brands as B2cBrand[]);
    this.services.currency.setPreloadedCurrencies(data.currencies as Currency[]);
    this.services.values.setPreloadedTopCategories(data.topCategories.map((item) => item.id));
    this.services.values.setPreloadedFeaturedBrands(data.featuredBrands.map((item) => item.brand.id));
    this.services.holidays.setPreloadedHolidays(data.holidayEvents as HolidayEvent[]);
    this.services.backendTranslations = data.translates;

    // after parameters set; cause applied parameters read

    const applied = this.services.parameters.parameters;
    const finalVarRate = this.calcFinalVatRate(applied.country);
    const isSameLanguage = i18next.language === applied.language;

    this.services.formatter.setDefaultVat(finalVarRate);
    this.services.customerChannel.setDefaultVat(finalVarRate);

    this.services.formatter.setDefaultCurrency(applied.currency);

    if (data.staticPage) {
      this.services.staticPages.updateStaticPage(data.staticPage?.pageName, data.staticPage);
    }

    if (!isSameLanguage) {
      await i18next.changeLanguage(applied.language);
    }

    if (!import.meta.env.SSR && import.meta.env.PUBLIC_SSR_CLIENT) {
      const icons = [] as string[];

      this.services.categories.getAll().forEach((id) => {
        const category = this.services.categories.getItem(id)!;
        if (category.icon) {
          icons.push(category.icon);
        }
      });

      if (this.services.customerChannel.logoUrl) {
        await IconResolver.instance.preloadOne(this.services.customerChannel.logoUrl, 'logo')
      }

      await IconResolver.instance.preload(icons);
    }

    await this.services.routes.load();
  }

  protected calcFinalVatRate(countryCode: string) {
    const country = this.services.countries.getByCode(countryCode)!;

    return country.vatRate === null
      ? defaultVatCoefficient
      : country.vatRate + defaultVatCoefficient;
    ;
  }

  protected clearServices() {
    this.services.preloaded.invalidate();
    this.services.categories.clear();
    this.services.brands.clear();
    // this.services.currency.clear();
    this.services.customerChannel.clear();
    this.services.routes.clear();
    this.services.cache.clear();
    this.services.values.clear();
  }

  protected getHash(params: { lang?: string }) {
    return [
      params.lang,
    ].join('-');
  }

  public invalidate() {
    if (this.initialization.state === 'initialized') {
      this.initialization.invalidated = true;
      this.services.suspense.clear();
    }
  }

  protected reload(url: string) {
    // Url.route(url, true);
    window.location.replace(url);
  }

  public async reinitialize() {
    const url = await this.getModifiedUrl();

    if (url) {
      this.invalidate();
      this.reload(url);
    }
  }

  protected async getModifiedUrl() {
    const parameters = this.services.parameters.parameters;
    const location = this.services.router.getCurrentRouteLocation();

    if (location) {
      if (location.name === 'dynamic-route') {
        const url = location.path;
        const normalized = Url.normalizeRoute(url);
        const prefix = `/${parameters.language}/`;
        const alternates = await this.services.routes.resolveAlternates(normalized);
        const found = [`/${normalized}`, ...alternates].find((item) => item.startsWith(prefix));

        return found;
      } else {
        const changed = structuredClone(location.params);
        if (changed.lang) {
          changed.lang = parameters.language;
        }

        if (location.name === "home-page") {
          return Url.to("home-page-with-lang", changed);
        }

        return Url.to(location.name, changed, location.search);
      }
    }
  }

  startAnalytics = () => {
    if (this.analyticsStarted) {
      return;
    }

    this.analyticsStarted = true;

    const done = () => {
      import('@urql/core');
      import('@/generated/graphql/b2c');
      import('@/generated/graphql/generic');
      import('@/generated/graphql/customer');

      window.postMessage({ type: 'idle_initialize' }, '*');
      window.postMessage({ type: 'analytics' }, '*');
    };

    const timeout = new Date().getTime() + 2000;
    const period = 500;
    const end = 200;
    const measurments = [] as Array<[number, number]>;

    const check = () => {
      requestIdleCallback((idle) => {
        const free = Math.min(50, idle.timeRemaining());
        const current = new Date().getTime();
        const start = current - period;

        measurments.push([current, free]);

        let sum = 0;
        for (let i = measurments.length - 1; i >= 0; i--) {
          const [time, free] = measurments[i];
          if (time < start || sum >= end) {
            break;
          }

          sum += free;
        }

        if (sum >= end || current >= timeout) {
          done();
        } else {
          setTimeout(check, free);
        }
      });
    };

    check();
  }
}
